import { useEffect, useRef, useState } from 'react';
import {
  loadNotifications,
  markNotificationsRead,
  unreadCount,
  type AppNotification,
} from '../../lib/notificationsService';

/**
 * Cloche de notifications dans l'en-tête de l'admin resto (Phase 3). Affiche le nombre
 * de non-lues ; au clic, déroule la liste et marque les non-lues comme lues.
 * Source : table `notifications` (créées côté serveur : demande reçue, option activée…).
 */
export default function NotificationBell({ tenantId }: { tenantId: string }) {
  const [items, setItems] = useState<AppNotification[]>([]);
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const refresh = () => {
    loadNotifications(tenantId).then(setItems);
  };

  useEffect(() => {
    refresh();
    // Rafraîchit toutes les 60 s tant que l'admin est ouvert.
    const t = setInterval(refresh, 60_000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  // Ferme le panneau si on clique en dehors.
  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (open && ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const unread = unreadCount(items);

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && unread > 0) {
      const ids = items.filter((n) => !n.readAt).map((n) => n.id);
      await markNotificationsRead(ids);
      // Reflète localement le « lu » sans recharger.
      setItems((list) => list.map((n) => (n.readAt ? n : { ...n, readAt: new Date().toISOString() })));
    }
  };

  const fmt = (iso: string) => {
    try {
      return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
    } catch {
      return '';
    }
  };
  const icon = (kind: string) => (kind === 'success' ? '✅' : kind === 'request' ? '📩' : 'ℹ️');

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={toggle}
        title="Notifications"
        className="relative rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold hover:bg-slate-50"
      >
        🔔
        {unread > 0 && (
          <span className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-red-500 px-1 text-[11px] font-bold text-white">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 z-50 mt-2 w-80 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-lg">
          <div className="border-b border-slate-100 px-4 py-2 text-sm font-bold">Notifications</div>
          <div className="max-h-96 overflow-y-auto">
            {items.length === 0 ? (
              <div className="px-4 py-6 text-center text-sm text-slate-400">Aucune notification.</div>
            ) : (
              items.map((n) => (
                <div key={n.id} className={'border-b border-slate-50 px-4 py-3 ' + (n.readAt ? '' : 'bg-brand-light/40')}>
                  <div className="flex items-start gap-2">
                    <span>{icon(n.kind)}</span>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold">{n.title}</div>
                      {n.body && <div className="mt-0.5 text-xs text-slate-500">{n.body}</div>}
                      <div className="mt-1 text-[11px] text-slate-400">{fmt(n.createdAt)}</div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
